<script setup>
/**
 * The live-chat targeting panel (PRD F5, mockup D). Trigger conditions are
 * read-only here - a single card summarising where, who, which devices, how
 * often, when, and the delay - and edited in the conditions modal. The inbox
 * and sender the campaign speaks as stay inline, because they are identity
 * rather than conditions.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CampaignFormField from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignFormField.vue';
import ConditionsDialog from './ConditionsDialog.vue';
import { CAMPAIGN_SELECT_CLASS } from 'dashboard/components-next/Campaigns/helpers/formFieldClasses';
import { buildConditionsSummary } from 'dashboard/components-next/Campaigns/helpers/campaignConditions';
import { useLiveChatTargeting } from 'dashboard/components-next/Campaigns/composables/useLiveChatTargeting';

const inboxId = defineModel('inboxId', { type: Number, default: null });
const senderId = defineModel('senderId', { type: Number, default: 0 });
const where = defineModel('where', { type: String, required: true });
const url = defineModel('url', { type: String, default: '' });
const timeOnPage = defineModel('timeOnPage', {
  type: [String, Number],
  default: 10,
});
const visitorType = defineModel('visitorType', {
  type: String,
  required: true,
});
const deviceType = defineModel('deviceType', { type: String, required: true });
const frequency = defineModel('frequency', { type: String, required: true });
const availability = defineModel('availability', {
  type: String,
  required: true,
});

const { t } = useI18n();

const conditionsDialogRef = ref(null);

const { inboxOptions, senderOptions } = useLiveChatTargeting(
  () => inboxId.value,
  {
    onSenderLoadError: error =>
      useAlert(
        error?.response?.message ??
          t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.ERROR_MESSAGE')
      ),
  }
);

const summary = computed(() =>
  buildConditionsSummary({
    where: where.value,
    url: url.value,
    visitorType: visitorType.value,
    deviceType: deviceType.value,
    frequency: frequency.value,
    availability: availability.value,
    timeOnPage: timeOnPage.value,
  })
);

const openConditions = () => conditionsDialogRef.value?.dialogRef?.open();
</script>

<template>
  <div class="flex flex-col gap-5">
    <section class="flex flex-col min-w-0 gap-3">
      <h3 class="mb-0 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.CONDITIONS.TITLE') }}
      </h3>

      <div
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-black2"
      >
        <div class="flex flex-col min-w-0 gap-1">
          <span class="font-medium text-label-small text-n-slate-11">
            {{ t('CAMPAIGN.BUILDER.CONDITIONS.WHERE.TITLE') }}
          </span>
          <span class="text-sm break-all text-n-slate-12">
            {{ t(summary.where.i18nKey, summary.where.params) }}
          </span>
        </div>

        <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span
            v-for="chip in summary.chips"
            :key="chip.key"
            class="inline-flex items-center gap-1 px-2 py-0.5 max-w-full font-medium rounded-full text-label-small bg-n-alpha-2 text-n-slate-11"
          >
            <Icon :icon="chip.icon" class="flex-shrink-0 size-3" />
            <span class="truncate">{{ t(chip.i18nKey, chip.params) }}</span>
          </span>
        </div>

        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-sliders-horizontal"
          class="w-full mt-1"
          :label="t('CAMPAIGN.BUILDER.CONDITIONS.EDIT')"
          @click="openConditions"
        />
      </div>
    </section>

    <fieldset class="flex flex-col min-w-0 gap-4">
      <legend class="mb-1.5 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.TARGETING.SENDER.TITLE') }}
      </legend>

      <CampaignFormField
        field-id="builderInbox"
        :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.LABEL')"
      >
        <ComboBox
          id="builderInbox"
          v-model="inboxId"
          :options="inboxOptions"
          :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.PLACEHOLDER')"
          :class="CAMPAIGN_SELECT_CLASS"
        />
      </CampaignFormField>

      <CampaignFormField
        field-id="builderSender"
        :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.LABEL')"
      >
        <ComboBox
          id="builderSender"
          v-model="senderId"
          :options="senderOptions"
          :disabled="!inboxId"
          :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.PLACEHOLDER')"
          :class="CAMPAIGN_SELECT_CLASS"
        />
      </CampaignFormField>
    </fieldset>

    <ConditionsDialog
      ref="conditionsDialogRef"
      v-model:where="where"
      v-model:url="url"
      v-model:visitor-type="visitorType"
      v-model:device-type="deviceType"
      v-model:frequency="frequency"
      v-model:availability="availability"
      v-model:time-on-page="timeOnPage"
    />
  </div>
</template>
